import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useSession } from './client';

/**
 * Состояние сессии и защита маршрутов.
 *
 * Провайдер существует, чтобы экраны не дёргали useSession поодиночке: иначе
 * каждый компонент заводит свою подписку, и при выходе часть экрана успевает
 * отрисоваться уже без пользователя.
 */
export interface SessionUser {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  timezone?: string;
}

interface SessionState {
  user: SessionUser | null;
  loading: boolean;
}

const SessionContext = createContext<SessionState>({ user: null, loading: true });

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, isPending } = useSession();

  const value: SessionState = {
    user: (data?.user as SessionUser | undefined) ?? null,
    loading: isPending,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useCurrentUser(): SessionState {
  return useContext(SessionContext);
}

/**
 * Защищённая часть приложения.
 *
 * Неавторизованный уходит на вход, а адрес, куда он шёл, запоминается в state
 * маршрута: после входа он попадёт именно туда, а не на общий экран. Иначе
 * ссылка на зону dashboard, открытая из письма или закладки, всегда приводила
 * бы не туда, куда вела.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useCurrentUser();
  const location = useLocation();

  if (loading) {
    return (
      <div
        className="text-foreground/75 flex h-dvh items-center justify-center text-sm"
        role="status"
        aria-live="polite"
      >
        Загрузка…
      </div>
    );
  }

  if (user === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

/** Обратное правило: вошедшему на экранах входа и регистрации делать нечего. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, loading } = useCurrentUser();

  if (loading) {
    return null;
  }

  if (user !== null) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}
